import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { Order } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Package, ChevronRight, ArrowLeft } from 'lucide-react';

export const OrderHistoryPage: React.FC = () => {
  const { data: ordersResponse, isLoading: loading, error } = useQuery({
    queryKey: ['orders'],
    queryFn: () => orderService.getOrders(),
  });

  if (loading) return <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center"><LoadingSpinner /></div>;

  if (error) return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center space-y-4">
      <h2 className="text-2xl font-black text-destructive tracking-tight uppercase">Access Restricted</h2>
      <p className="text-muted-foreground font-medium">Unable to retrieve your order vault at this time.</p>
      <Button onClick={() => window.location.reload()} className="rounded-xl">Retry Vault Access</Button>
    </div>
  );

  const orders = ordersResponse?.orders || [];

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">Order Repository</h1>
          <p className="text-gray-500 font-medium tracking-tight">Access your archive of premium acquisitions.</p>
        </div>
        <Link to="/products" className="text-sm font-bold text-primary flex items-center gap-2 hover:gap-3 transition-all mb-1">
          <ArrowLeft className="size-4" /> Return to Gallery
        </Link>
      </div>

      {orders.length === 0 ? (
        <div className="py-32 text-center space-y-8 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] border border-dashed border-border/60">
          <div className="size-24 bg-white dark:bg-black rounded-[2rem] flex items-center justify-center mx-auto shadow-sm">
            <Package className="size-10 text-gray-300" />
          </div>
          <div className="space-y-2 px-6">
            <h2 className="text-2xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Your archive is vacant</h2>
            <p className="text-gray-500 font-medium tracking-tight max-w-xs mx-auto">Seal your first acquisition and it will appear here for eternity.</p>
          </div>
          <Button asChild size="lg" className="rounded-2xl h-14 px-10 font-black shadow-xl shadow-primary/10">
            <Link to="/products">Explore Collection</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {orders.map((order: Order) => (
            <div key={order.id} className="group bg-white dark:bg-black rounded-[2rem] p-8 border border-border/40 hover:border-primary/40 transition-all duration-500 shadow-sm hover:shadow-2xl hover:shadow-primary/5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-gray-100 dark:border-gray-800">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-black text-[#111418] dark:text-white uppercase tracking-tight">Order #{order.id}</h3>
                    <Badge className={`uppercase text-[9px] font-black tracking-[0.1em] px-3 py-1 rounded-full ${order.status === 'DELIVERED' ? 'bg-emerald-400/10 text-emerald-500' :
                      order.status === 'PROCESSING' ? 'bg-amber-400/10 text-amber-500' : 'bg-primary/10 text-primary'
                      }`}>
                      {order.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest leading-none">
                    Acquired on {new Date(order.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Investment</span>
                  <span className="text-2xl font-black text-primary">${order.total_amount.toFixed(2)}</span>
                </div>
              </div>

              <div className="pt-6 mt-2 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                <Button variant="ghost" size="sm" className="rounded-xl font-black text-[10px] uppercase tracking-widest gap-2 hover:bg-gray-50 dark:hover:bg-gray-900" asChild>
                  <Link to={`/orders/${order.id}`}>
                    Details <ChevronRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
